import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { unwrapResult } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';

import {
  createResourceSlot,
  deleteResourceSlot,
  orderBudgetExpenses,
  updateResourceSlotOrder
} from 'store/v1/productions/productions.actions.js';
import { displayAlert, openModal } from 'store/v1/ui/ui.actions.js';
import { Capability } from '__types__';
import {
  RestrictedDropdownItem,
  RestrictedDropdownV5
} from '../../../buttons/RestrictedDropdown/RestrictedDropdown';
import { Button } from 'v5/design-system/Button';
import { Copy, MoreHorizontal, Trash2 } from 'lucide-react';
import { myServices } from 'v5/services/services.connected.app';
import { DropdownMenuItem } from 'v5/design-system/DropdownMenu';

const buildDuplicateSlotPayload = slot => ({
  // Duplicate only the slot definition; resource assignments are not copied.
  color: slot.color,
  group_id: slot.group_id,
  include_in_calculation: slot.include_in_calculation,
  is_location: !!slot.is_location,
  name: slot.name,
  note: slot.note,
  query: slot.query,
  estimate_rate_amount: slot.estimate_rate_amount,
  estimate_rate_currency: slot.estimate_rate_currency,
  estimate_rate_interval: slot.estimate_rate_interval,
  estimate_rate_quantity: slot.estimate_rate_quantity,
  estimate_rate_agency_fee: slot.estimate_rate_agency_fee,
  estimate_rate_payroll_fee: slot.estimate_rate_payroll_fee,
  estimate_rate_processing_fee: slot.estimate_rate_processing_fee,
  ...(slot.slot_category_id
    ? { slot_category_id: slot.slot_category_id }
    : { slot_category: slot.slot_category })
});

const ResourceSlotOptions = ({ slot }) => {
  const dispatch = useDispatch();
  const slugAppScope = myServices.appScopeService.useSlugAppScope();
  const production = useSelector(
    state => state.productions.data[slot.production_id]
  );

  const onSlotDuplicate = () => {
    const payload = buildDuplicateSlotPayload(slot);

    dispatch(createResourceSlot({ production_id: slot.production_id }, payload))
      .then(action => {
        if (action.error) {
          throw action.error;
        }

        const duplicatedSlot = action.result;
        if (!duplicatedSlot?.id) {
          throw new Error('Failed to duplicate role');
        }

        const resourceRows = (production?.resource_slots || [])
          .filter(
            item =>
              !item.archived && String(item.group_id) === String(slot.group_id)
          )
          .map(item => ({
            type: 'RESOURCE',
            id: item.id,
            order: Number(item.order) || 0
          }));

        const expenseRows = (production?.budget_expenses || [])
          .filter(
            item =>
              !item.archived && String(item.group_id) === String(slot.group_id)
          )
          .map(item => ({
            type: 'EXPENSE',
            id: item.id,
            order: Number(item.order) || 0
          }));

        const groupRows = orderBy(
          [...resourceRows, ...expenseRows],
          ['order', 'id'],
          ['asc', 'asc']
        );

        const sourceIndex = groupRows.findIndex(
          row => row.type === 'RESOURCE' && String(row.id) === String(slot.id)
        );

        const sourceOrder = Number(slot.order) || 0;
        const insertionIndex =
          sourceIndex >= 0
            ? sourceIndex + 1
            : groupRows.reduce(
                (result, row, index) =>
                  row.order <= sourceOrder ? index + 1 : result,
                0
              );

        const reorderedRows = [
          ...groupRows.slice(0, insertionIndex),
          { type: 'RESOURCE', id: duplicatedSlot.id },
          ...groupRows.slice(insertionIndex)
        ];

        const reorderedResourceSlots = [];
        const reorderedExpenseSlots = [];

        reorderedRows.forEach((row, order) => {
          const entry = {
            id: row.id,
            order,
            group_id: slot.group_id
          };

          if (row.type === 'RESOURCE') {
            reorderedResourceSlots.push(entry);
          } else {
            reorderedExpenseSlots.push(entry);
          }
        });

        const reorderActions = [
          dispatch(
            updateResourceSlotOrder(
              { production_id: slot.production_id },
              reorderedResourceSlots
            )
          )
        ];

        if (reorderedExpenseSlots.length) {
          reorderActions.push(
            dispatch(
              orderBudgetExpenses(
                { production_id: slot.production_id },
                reorderedExpenseSlots
              )
            )
          );
        }

        return Promise.all(reorderActions).then(results => {
          const failedAction = results.find(result => result?.error);
          if (failedAction) {
            throw failedAction.error;
          }
        });
      })
      .catch(error => {
        const message =
          error?.Message || error?.message || 'Something went wrong';
        dispatch(displayAlert('error', message));
      });
  };

  const onSlotDelete = () => {
    const canDelete =
      !slot.resource_slot_assignments ||
      !slot.resource_slot_assignments.length ||
      !slot.resource_slot_assignments.some(assignment => !assignment.archived);

    canDelete
      ? dispatch(
          openModal(
            'ConfirmModal',
            {
              title: 'Are you sure you want to delete this role?',
              confirmLabel: 'Confirm delete',
              onConfirm: () =>
                dispatch(
                  deleteResourceSlot({
                    production_id: slot.production_id,
                    resource_slot_id: slot.id
                  })
                )
                  .then(unwrapResult)
                  .then(() => {
                    dispatch(displayAlert('success', 'Role removed'));
                  })
                  .catch(() => {
                    dispatch(displayAlert('error', 'Something went wrong'));
                  })
            },
            slugAppScope
          )
        )
      : dispatch(
          openModal(
            'AlertModal',
            {
              title: 'Cannot delete role if a resource is assigned',
              description:
                'A role cannot be removed if it has a resource assigned to it. You’ll need to remove the resource from the role before you can delete it.'
            },
            slugAppScope
          )
        );
  };

  return (
    <div className="tailwind-root contents">
      <RestrictedDropdownV5
        capabilitiesAtLeastOne={[
          Capability.PRODUCTION_READ,
          Capability.PRODUCTION_UPDATE
        ]}
        trigger={
          <Button variant="ghost" size="sm" layout="icon">
            <MoreHorizontal />
          </Button>
        }
        align="end"
      >
        <RestrictedDropdownItem capabilities={Capability.PRODUCTION_UPDATE}>
          <DropdownMenuItem onSelect={onSlotDuplicate}>
            <Copy />
            Duplicate Role
          </DropdownMenuItem>
        </RestrictedDropdownItem>
        <RestrictedDropdownItem capabilities={Capability.PRODUCTION_UPDATE}>
          <DropdownMenuItem onSelect={onSlotDelete}>
            <Trash2 className="text-fg-danger" />
            Remove role
          </DropdownMenuItem>
        </RestrictedDropdownItem>
      </RestrictedDropdownV5>
    </div>
  );
};

ResourceSlotOptions.propTypes = {
  slot: PropTypes.object
};

export default ResourceSlotOptions;
